import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { getInboxConversations } from "api/api.service";
import usePolling from "../inbox/usePolling";
import useInboxStream from "../inbox/useInboxStream";
import ConversationList from "../inbox/ConversationList";
import ChatPane from "../inbox/ChatPane";
// Licensing first: the inbox reuses its buttons, chips and error card, and
// overrides a few of them, which needs to come later in the cascade.
import "../licensing/licensing.scss";
import "../inbox/inbox.scss";

// With the live stream connected, polling is only a safety net.
const LIST_POLL_MS = { live: 30000, fallback: 10000 };

/**
 * Inbox: WhatsApp and the website chat.
 *
 * Every conversation the AI sales agent is having, on either channel, live,
 * with a way for a person to take one over, reply, and hand it back. Website
 * chats appear once the visitor has given an email (or someone is needed). Chats that need a person
 * sort first. The open conversation is in the URL (?conversation=<id>), which
 * is also where the "needs a person" email links.
 */
export default function Inbox() {
  const history = useHistory();
  const location = useLocation();
  const selectedId = Number(new URLSearchParams(location.search).get("conversation")) || null;

  const [filter, setFilter] = useState("all");
  const [channel, setChannel] = useState("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  // Search waits for a pause in typing instead of fetching per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(
    (signal) =>
      getInboxConversations(
        { ...(filter !== "all" ? { filter } : {}), ...(channel !== "all" ? { channel } : {}), ...(query ? { q: query } : {}) },
        signal,
      ),
    [filter, channel, query],
  );
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  const [chatTick, setChatTick] = useState(0);
  const listRefreshTimer = useRef(null);

  // Live updates: an event says which conversation changed. The list
  // re-fetches (coalesced, so a burst of events is one request) and the open
  // chat re-fetches when it is the one that changed.
  const onLiveEvent = useCallback((event) => {
    clearTimeout(listRefreshTimer.current);
    listRefreshTimer.current = setTimeout(() => listRef.current?.refresh(), 250);
    if (event?.conversationId === selectedRef.current) setChatTick((n) => n + 1);
  }, []);
  const { connected: live } = useInboxStream(onLiveEvent);
  useEffect(() => () => clearTimeout(listRefreshTimer.current), []);

  const list = usePolling(fetchList, `list:${filter}:${channel}:${query}`, live ? LIST_POLL_MS.live : LIST_POLL_MS.fallback);
  const listRef = useRef(list);
  listRef.current = list;

  const select = useCallback(
    (id) => history.push({ search: id ? `?conversation=${id}` : "" }),
    [history],
  );

  // No grant: the server answered 403. Say what is missing, not "no chats".
  if (list.error?.Status === 403) {
    return (
      <div className="p-4 mt-3 mcap-lic mcap-inbox">
        <div className="mcap-lic-error-card" role="alert">
          <h3>Inbox access not granted.</h3>
          <p>
            This account can use MCAP but not the inbox, which shows every lead&apos;s contact details and
            messages. An administrator can grant Inbox access from the admin console.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`mcap-lic mcap-inbox${selectedId ? " is-chat-open" : ""}`}>
      <ConversationList
        live={live}
        list={list}
        filter={filter}
        onFilter={setFilter}
        channel={channel}
        onChannel={setChannel}
        search={search}
        onSearch={setSearch}
        selectedId={selectedId}
        onSelect={select}
      />
      {selectedId ? (
        <ChatPane
          key={selectedId}
          conversationId={selectedId}
          live={live}
          liveTick={chatTick}
          onChanged={list.refresh}
          onOpen={select}
          onBack={() => select(null)}
        />
      ) : (
        <section className="mcap-inbox-chat mcap-inbox-placeholder">
          <div>
            <h3>Pick a conversation</h3>
            <p>Chats marked “Needs a person” are waiting for someone on the team.</p>
          </div>
        </section>
      )}
    </div>
  );
}
